import { IMG } from "@/lib/site";
import Reveal from "@/components/site/Reveal";
import SectionCTA from "@/components/site/SectionCTA";
import FAQ from "@/components/site/FAQ";
import { Globe, Video, Clock, Heart } from "lucide-react";
import useSeo from "@/lib/useSeo";
import { useFaqs } from "@/lib/faqs";
import { useT } from "@/lib/i18n";

const SesionesOnline = () => {
  const t = useT();
  const { online } = useFaqs();
  useSeo({
    title: {
      es: "Sesiones Online en Español · España y Latinoamérica | Método TAO",
      ca: "Sessions Online · Espanya i Llatinoamèrica | Mètode TAO",
    },
    description: {
      es: "Sesiones online de acompañamiento cuerpo-mente por videollamada para procesos oncológicos, fibromialgia, fatiga y dolor crónico. En español, atendiendo España, México, Argentina, Colombia, Chile y todo el mundo hispanohablante.",
      ca: "Sessions online d'acompanyament cos-ment per videotrucada per a processos oncològics, fibromiàlgia, fatiga i dolor crònic. Atenent Espanya, Mèxic, Argentina, Colòmbia, Xile i tot el món hispanoparlant.",
    },
    path: "/sesiones-online",
  });
  // FAQ schema is injected by the <FAQ> component itself — do NOT duplicate here.

  const features = [
    { icon: Globe, t: t("online.feature1.t"), d: t("online.feature1.d") },
    { icon: Video, t: t("online.feature2.t"), d: t("online.feature2.d") },
    { icon: Clock, t: t("online.feature3.t"), d: t("online.feature3.d") },
    { icon: Heart, t: t("online.feature4.t"), d: t("online.feature4.d") },
  ];

  const ideal = [
    t("online.ideal.1"),
    t("online.ideal.2"),
    t("online.ideal.3"),
    t("online.ideal.4"),
    t("online.ideal.5"),
    t("online.ideal.6"),
  ];

  return (
  <div className="bg-ivory">
    <section className="pt-12 md:pt-20 pb-16 md:pb-24" data-testid="page-online">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        <Reveal className="lg:col-span-7">
          <p className="text-[11px] tracking-[0.32em] uppercase text-sage mb-6">{t("online.eyebrow")}</p>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-charcoal leading-[0.98]">
            {t("online.title1")} <span className="italic text-terracotta">{t("online.title2")}</span>{t("online.title3")}
          </h1>
          <p className="mt-7 text-lg md:text-xl leading-relaxed text-charcoal/75 font-light max-w-xl">
            {t("online.lead")}
          </p>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-5">
          <div className="aspect-[4/5] rounded-tr-[4rem] rounded-bl-[4rem] overflow-hidden bg-sand">
            <img src={IMG.onlineYogaSession} alt="Sesión online de yoga oncológico Método TAO" className="w-full h-full object-cover" fetchPriority="high" decoding="async" />
          </div>
        </Reveal>
      </div>
    </section>

    <section className="py-20 md:py-28 bg-sand/30 grain">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {features.map((f, i) => (
            <Reveal key={f.t} delay={i * 0.06}>
              <div className="bg-ivory rounded-3xl p-8 border border-sand/60 h-full">
                <span className="w-11 h-11 rounded-full bg-sand flex items-center justify-center text-olive">
                  <f.icon size={18} />
                </span>
                <h3 className="mt-6 font-serif text-2xl text-charcoal">{f.t}</h3>
                <p className="mt-3 text-charcoal/65 text-[14px] leading-relaxed">{f.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="py-20 md:py-28">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 md:gap-16 items-center">
        <Reveal className="md:col-span-5">
          <p className="text-[11px] tracking-[0.32em] uppercase text-sage mb-5">{t("online.idealEyebrow")}</p>
          <h2 className="font-serif text-4xl md:text-5xl text-charcoal leading-[1.05]">
            {t("online.idealTitle1")} <span className="italic">{t("online.idealTitle2")}</span>.
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="md:col-span-7">
          <ul className="grid sm:grid-cols-2 gap-x-6">
            {ideal.map((p) => (
              <li key={p} className="flex items-center gap-3 py-4 border-b border-charcoal/10 text-charcoal/85">
                <span className="w-2 h-2 rounded-full bg-terracotta" />
                <span className="text-[15px]">{p}</span>
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </section>

    <FAQ
      eyebrow={t("online.faqEyebrow")}
      title={t("online.faqTitle")}
      items={online}
    />

    <SectionCTA title={t("online.ctaTitle")} body={t("online.ctaBody")} />
  </div>
  );
};

export default SesionesOnline;
